/**
 * PATH: packages/ui/src/lib/chunkErrorHandler.ts
 * PURPOSE: Handle stale chunk 404s after deployment across all hubs.
 *
 * PROBLEM: After a deploy, Vite produces new content-hashed filenames.
 *          Tabs open from before the deploy still reference old chunk names,
 *          causing 404s when lazy-loading routes.
 *
 * SOLUTION: Detect chunk load errors and auto-reload once with a cooldown
 *           to prevent infinite loops. Shows a manual prompt if the auto-reload
 *           already happened recently.
 *
 * STAY-LOGGED-IN ACROSS DEPLOYS (operator report 2026-06-03 "expected
 * one-time deploy bounce — can we avoid this?"):
 *   The reload itself is unavoidable (the old tab MUST fetch the new
 *   bundle), but the SIGN-OUT during that reload is not. After ~60 min the
 *   short-lived `quotech_auth` access cookie has expired; if we reload while
 *   it's stale, the post-reload boot fires API calls that 401 before the
 *   silent refresh wins the race, and the operator lands on the sign-in
 *   card. So — exactly like the VersionWatcher "Refresh now" path already
 *   does via src/shared/refreshBeforeReload.ts — we PRE-REFRESH the access
 *   cookie (best-effort, time-boxed, never throws) BEFORE reloading. The
 *   tab then comes back already authenticated. A failed/again-skipped
 *   refresh leaves behaviour identical to before (boot-time recovery or, if
 *   the session is genuinely dead, login). This is the shared twin of the
 *   master SPA's helper, so it covers every hub that installs this handler.
 *
 * USAGE (in any hub's main.tsx):
 *   import { installChunkErrorHandler } from '@quotech/ui'
 *   installChunkErrorHandler()
 *   // or, to plug in an app-specific refresh (e.g. the v1 token path):
 *   installChunkErrorHandler({ beforeReload: () => myRefresh() })
 */

const CHUNK_ERROR_RELOAD_KEY = 'quotech_chunk_error_reload'
const CHUNK_ERROR_COOLDOWN_MS = 30_000 // 30s between auto-reload attempts

const AUTH_COOKIE_NAME = 'quotech_auth'
const REFRESH_COOKIE_NAME = 'quotech_refresh'

function resolveQuotechBackend(): string {
  const explicit =
    (typeof import.meta !== 'undefined' &&
      (import.meta as unknown as { env?: Record<string, string | undefined> })?.env
        ?.VITE_QUOTECH_BACKEND) ||
    ''
  if (explicit) return explicit
  if (typeof window !== 'undefined') {
    const host = window.location.hostname
    if (host.endsWith('.quotech.ai')) {
      return ''
    }
  }
  return 'https://quotech-backend.quotech.ai'
}

const QUOTECH_BACKEND = resolveQuotechBackend()

function isAuthV2Enabled(): boolean {
  return (
    String(
      (import.meta as unknown as { env?: Record<string, string | undefined> })?.env
        ?.VITE_QUOTECH_AUTHV2 || '',
    )
      .toLowerCase()
      .trim() === 'true'
  )
}

const V2_REFRESH_PATH = '/api/auth/v2/refresh/'
const V1_REFRESH_PATH = '/api/auth/refresh-token/'
const PRE_RELOAD_REFRESH_TIMEOUT_MS = 2_500

/** App-supplied override for the pre-reload refresh (e.g. the master SPA's
 *  v1 token-persisting helper). Set via installChunkErrorHandler(options). */
let beforeReloadHook: (() => Promise<void> | void) | null = null

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))
  if (!m) return null
  try {
    return decodeURIComponent(m[1])
  } catch {
    return m[1]
  }
}

/** True when the access JWT is missing/malformed/expired (or within the
 *  clock-skew window) — i.e. a reload right now would race a boot 401. */
function isAccessExpired(token: string | null, nowMs = Date.now(), skewMs = 30_000): boolean {
  if (!token) return true
  const parts = token.split('.')
  if (parts.length !== 3) return true
  try {
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    const payload = JSON.parse(json) as { exp?: number }
    const exp = payload?.exp
    if (typeof exp !== 'number' || !Number.isFinite(exp)) return true
    return exp * 1000 <= nowMs + skewMs
  } catch {
    return true
  }
}

/** Any evidence of a prior signed-in session — avoids pestering the backend
 *  with a refresh round-trip for a genuinely anonymous visitor. */
function hasSessionEvidence(): boolean {
  try {
    if (readCookie(AUTH_COOKIE_NAME)) return true
    if (readCookie(REFRESH_COOKIE_NAME)) return true
    if (typeof document !== 'undefined' && document.cookie.includes('quotech_remember_me=1'))
      return true
    if (typeof localStorage !== 'undefined') {
      if (localStorage.getItem('UserData')) return true
      if (localStorage.getItem('refreshToken')) return true
    }
  } catch {
    /* private mode / quota — fall through */
  }
  return false
}

/**
 * Best-effort silent refresh BEFORE a deploy-driven reload. Resolves (never
 * rejects) once the refresh has completed, timed out, or been skipped — the
 * caller reloads regardless of outcome.
 */
async function refreshBeforeReload(timeoutMs = PRE_RELOAD_REFRESH_TIMEOUT_MS): Promise<void> {
  // App override wins (e.g. the master SPA's v1+v2 helper).
  if (beforeReloadHook) {
    try {
      await beforeReloadHook()
    } catch {
      /* best-effort — fall through to reload */
    }
    return
  }
  if (typeof fetch === 'undefined') return
  // Fresh access cookie → nothing to do.
  if (!isAccessExpired(readCookie(AUTH_COOKIE_NAME))) return
  // No prior session → don't fire a pointless anon refresh.
  if (!hasSessionEvidence()) return

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    if (isAuthV2Enabled()) {
      await fetch(`${QUOTECH_BACKEND}${V2_REFRESH_PATH}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
        signal: controller.signal,
      })
    } else {
      const refreshToken =
        (typeof localStorage !== 'undefined' && localStorage.getItem('refreshToken')) ||
        readCookie(REFRESH_COOKIE_NAME)
      if (!refreshToken) return
      const resp = await fetch(`${QUOTECH_BACKEND}${V1_REFRESH_PATH}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: refreshToken }),
        signal: controller.signal,
      })
      if (resp.ok) {
        const data = await resp.json().catch(() => null)
        const newToken = data?.access || data?.access_token
        if (newToken && typeof localStorage !== 'undefined') {
          try {
            localStorage.setItem('IdToken', newToken)
            localStorage.setItem('accessTokennew', newToken)
          } catch {
            /* private mode / quota */
          }
        }
      }
    }
  } catch {
    /* timeout / network / 401 — best-effort; fall through to reload */
  } finally {
    clearTimeout(timer)
  }
}

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false

  const str = String(error).toLowerCase()
  const msg = ((error as Error)?.message || '').toLowerCase()

  return (
    str.includes('dynamically imported module') ||
    msg.includes('dynamically imported module') ||
    str.includes('loading chunk') ||
    msg.includes('loading chunk') ||
    str.includes('loading css chunk') ||
    msg.includes('loading css chunk') ||
    str.includes('failed to fetch dynamically') ||
    msg.includes('failed to fetch dynamically') ||
    // Vite specific
    str.includes('preloaderror') ||
    msg.includes('preloaderror') ||
    // Generic 404 on .js asset
    (str.includes('404') && str.includes('.js'))
  )
}

function showReloadBanner(): void {
  if (document.getElementById('quotech-chunk-banner')) return

  const banner = document.createElement('div')
  banner.id = 'quotech-chunk-banner'
  banner.style.cssText = `
    position:fixed;top:0;left:0;right:0;z-index:50;
    background:linear-gradient(135deg,#0d9488,#0f766e);
    color:#fff;padding:10px 16px;text-align:center;
    font-family:system-ui,-apple-system,sans-serif;font-size:13px;
    box-shadow:0 2px 8px rgba(0,0,0,.15);
  `
  banner.innerHTML = `
    <span>A new version has been deployed.</span>
    <button id="quotech-chunk-reload" style="
      background:#fff;color:#0f766e;border:none;padding:3px 12px;
      border-radius:6px;cursor:pointer;font-weight:600;margin-left:8px;
      font-size:13px;
    ">Refresh</button>
  `
  document.body.prepend(banner)
  document.getElementById('quotech-chunk-reload')?.addEventListener('click', () => {
    localStorage.removeItem(CHUNK_ERROR_RELOAD_KEY)
    // Pre-refresh so the manual reload doesn't bounce an expired-cookie tab
    // to the sign-in card either.
    void refreshBeforeReload().then(() => window.location.reload())
  })
}

async function handleChunkLoadError(_error: unknown): Promise<void> {
  const lastReload = localStorage.getItem(CHUNK_ERROR_RELOAD_KEY)
  const now = Date.now()

  // Cooldown — don't auto-reload twice within 30s
  if (lastReload && now - parseInt(lastReload, 10) < CHUNK_ERROR_COOLDOWN_MS) {
    showReloadBanner()
    return
  }

  localStorage.setItem(CHUNK_ERROR_RELOAD_KEY, now.toString())

  // Clear service worker caches
  if ('caches' in window) {
    caches.keys().then(names => names.forEach(n => caches.delete(n)))
  }

  // Refresh the access cookie BEFORE reloading so the tab returns
  // authenticated instead of racing a boot-time 401 → sign-in bounce.
  await refreshBeforeReload()
  window.location.reload()
}

/**
 * Programmatic entry to the SAME one-shot reload the global listeners use,
 * for callers that detect deploy-staleness themselves. First user: the bills
 * PDF viewer — pdf.js swallows its worker-load 404 internally (getDocument
 * just hangs), so the window error/unhandledrejection listeners never fire
 * and the viewer silently degraded to the native <iframe> with no highlight
 * layer (operator 2026-07-25 "no bounding box nothing"). After probing that
 * its own hashed assets are gone from the server, it calls this instead.
 * Cooldown + banner + pre-reload auth refresh semantics are identical to the
 * automatic path.
 */
export function recoverFromStaleDeploy(): Promise<void> {
  return handleChunkLoadError(new Error('stale deploy: hashed assets no longer on server'))
}

export interface ChunkErrorHandlerOptions {
  /** App-specific pre-reload refresh. When provided it REPLACES the built-in
   *  best-effort v2 cookie refresh — use it when the app owns a richer
   *  refresh (e.g. the master SPA persists v1 localStorage tokens too). */
  beforeReload?: () => Promise<void> | void
}

/**
 * Install global error handlers that catch chunk-load 404s
 * and auto-reload the page. Safe to call multiple times.
 */
let installed = false

export function installChunkErrorHandler(options: ChunkErrorHandlerOptions = {}): void {
  // Allow a late caller to register the refresh hook even if handlers were
  // already installed (idempotent for the listeners themselves).
  if (options.beforeReload) beforeReloadHook = options.beforeReload
  if (installed) return
  installed = true

  window.addEventListener('error', (event) => {
    if (isChunkLoadError(event.error)) {
      event.preventDefault()
      void handleChunkLoadError(event.error)
    }
  })

  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault()
      void handleChunkLoadError(event.reason)
    }
  })
}

// Exported for unit tests + advanced callers that want to pre-refresh
// outside the chunk-error path.
export { refreshBeforeReload as __refreshBeforeReload }
